import React from 'react';
import { Button, Select, Space } from 'antd';
import { DeleteOutlined, PlusOutlined, SortAscendingOutlined } from '@ant-design/icons';

/**
 * DataSet 查詢排序編輯器 - 支援多欄位 ORDER BY，每欄位可選 ASC / DESC
 *
 * @param {Array}    orderByFields           - [{ id, fieldName, direction }]
 * @param {function} onOrderByFieldsChange   - (newFields) => void
 * @param {Array}    dataSetColumns          - [{ columnName, displayName, dataType }]
 * @param {function} t
 */
const DataSetQueryOrderByEditor = ({
  orderByFields = [],
  onOrderByFieldsChange,
  dataSetColumns = [],
  t
}) => {
  const handleAdd = () => {
    const newField = {
      id: `orderBy${Date.now()}`,
      fieldName: '',
      direction: 'ASC'
    };
    onOrderByFieldsChange([...orderByFields, newField]);
  };

  const handleChange = (id, key, value) => {
    onOrderByFieldsChange(
      orderByFields.map(f => f.id === id ? { ...f, [key]: value } : f)
    );
  };

  const handleDelete = (id) => {
    onOrderByFieldsChange(orderByFields.filter(f => f.id !== id));
  };

  return (
    <div>
      <div style={{
        border: '1px solid #d9d9d9',
        borderRadius: '6px',
        padding: '8px',
        minHeight: '48px',
        backgroundColor: '#fafafa'
      }}>
        {orderByFields.length === 0 && (
          <div style={{ textAlign: 'center', padding: '8px', color: '#bbb', fontSize: '13px' }}>
            {t('workflowDesigner.dataSet.noOrderByFields')}
          </div>
        )}

        {orderByFields.map((field, index) => (
          <div
            key={field.id}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              marginBottom: index < orderByFields.length - 1 ? '6px' : 0,
              padding: '6px 8px',
              backgroundColor: '#fff',
              border: '1px solid #e8e8e8',
              borderRadius: '4px'
            }}
          >
            {/* 排序優先級標籤 */}
            <span style={{
              fontSize: '11px',
              color: '#888',
              minWidth: '18px',
              textAlign: 'center',
              fontWeight: 'bold'
            }}>
              {index + 1}
            </span>

            {/* 欄位選擇 */}
            <Select
              style={{ flex: 1 }}
              size="small"
              placeholder={t('workflowDesigner.dataSet.selectOrderByField')}
              value={field.fieldName || undefined}
              onChange={(value) => handleChange(field.id, 'fieldName', value)}
              showSearch
              filterOption={(input, option) =>
                (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
              }
              options={dataSetColumns.map(col => ({
                value: col.columnName,
                label: col.displayName ? `${col.displayName} (${col.columnName})` : col.columnName
              }))}
            />

            {/* 排序方向 */}
            <Select
              style={{ width: '110px' }}
              size="small"
              value={field.direction || 'ASC'}
              onChange={(value) => handleChange(field.id, 'direction', value)}
            >
              <Select.Option value="ASC">
                <Space size={4}>
                  <SortAscendingOutlined />
                  {t('workflowDesigner.dataSet.orderAsc')}
                </Space>
              </Select.Option>
              <Select.Option value="DESC">
                <Space size={4}>
                  <SortAscendingOutlined rotate={180} />
                  {t('workflowDesigner.dataSet.orderDesc')}
                </Space>
              </Select.Option>
            </Select>

            {/* 刪除 */}
            <Button
              type="text"
              danger
              size="small"
              icon={<DeleteOutlined />}
              onClick={() => handleDelete(field.id)}
            />
          </div>
        ))}
      </div>

      <Button
        type="dashed"
        icon={<PlusOutlined />}
        onClick={handleAdd}
        style={{ width: '100%', marginTop: '8px' }}
        size="small"
        disabled={dataSetColumns.length === 0}
      >
        {t('workflowDesigner.dataSet.addOrderByField')}
      </Button>

      {/* 預覽生成的 ORDER BY */}
      {orderByFields.length > 0 && orderByFields.some(f => f.fieldName) && (
        <div style={{
          marginTop: '8px',
          padding: '6px 10px',
          backgroundColor: '#f0f5ff',
          borderRadius: '4px',
          fontSize: '12px',
          color: '#555',
          fontFamily: 'monospace'
        }}>
          ORDER BY {orderByFields
            .filter(f => f.fieldName)
            .map(f => `${f.fieldName} ${f.direction}`)
            .join(', ')}
        </div>
      )}
    </div>
  );
};

export default DataSetQueryOrderByEditor;
